import {
    Compartment,
    EditorState,
    type EditorStateConfig,
} from "@codemirror/state";
import {
    EditorView,
    type EditorViewConfig,
    type ViewUpdate,
} from "@codemirror/view";
import {css, StyleSheet} from "aphrodite";
import {useEffect, useMemo, useRef} from "react";

import {
    FilenameFacet,
    getLintExtension,
    getPythonExtensions,
    type Diagnostic,
} from "./code-mirror-extensions.ts";

type Props = {
    // The initialCode to be rendered in the editor. If initialCode or filename
    // changes, the editor state will be re-created, which will reset the user's
    // selection and cursor, so this should only be done when the user switches
    // files (i.e. this should not be used as a controlled component).
    initialCode: string;

    // The name of the file currently being edited. This doesn't appear in the
    // editor, but this is used to determine when we should blow away the editor
    // state and re-create it. Also, it's passed back to the parent in onCodeChange.
    filename: string;

    // A stable identifier for a file that doesn't change when the file is renamed.
    // Used to preserve editor state when switching between files.
    fileKey: number;

    // A callback that is called when the code changes.
    onCodeChange: (filename: string, code: string) => void;

    // A linter for the code; accepts a string of code and returns an array
    // of Diagnostics, including the error message, severity, and to/from position.
    onLintCode: (code: string) => Promise<Diagnostic[]>;
};

/**
 * A React component that provides a wrapper for a CodeMirror editor.
 */
const CodeMirrorEditor = ({
    filename,
    fileKey,
    initialCode,
    onCodeChange,
    onLintCode,
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorViewRef = useRef<EditorView | null>(null);

    const fileStatesRef = useRef(
        new Map<
            number,
            {
                selection: EditorStateConfig["selection"];
                scrollSnapshot: EditorViewConfig["scrollTo"];
            }
        >(),
    );
    const previousFileKeyRef = useRef(fileKey);
    if (
        previousFileKeyRef.current !== fileKey &&
        editorViewRef.current != null
    ) {
        fileStatesRef.current.set(previousFileKeyRef.current, {
            selection: editorViewRef.current.state.selection,
            scrollSnapshot: editorViewRef.current.scrollSnapshot(),
        });
        previousFileKeyRef.current = fileKey;
    }

    // The lifecycle of the update listener is managed separately from the other
    // extensions, because we don't want to blow away the editor state whenever
    // the onCodeChange prop is updated; instead, we use a Compartment to update
    // the editor whenever the onCodeChange prop is updated. See the effect
    // below.
    const updateListenerCompartment = useMemo(() => new Compartment(), []);
    const updateListener = useMemo(
        () =>
            EditorView.updateListener.of((viewUpdate: ViewUpdate) => {
                if (!viewUpdate.docChanged) {
                    return;
                }
                const filename = viewUpdate.state.facet(FilenameFacet);
                const updatedCode = viewUpdate.state.doc.toString();
                onCodeChange(filename, updatedCode);
            }),
        [onCodeChange],
    );
    // Use a ref for this extension to avoid re-creating the editor state
    // when it changes. We never update this ref again, we just don't want it
    // to count as a dependency for the memoized state below.
    const updateListenerExtension = useRef(
        updateListenerCompartment.of(updateListener),
    );

    // Same as above; using a compartment to update the linter when onLintCode
    // changes, and a ref to avoid re-creating the editor state.
    const linterCompartment = useMemo(() => new Compartment(), []);
    const lintExtension = useRef(
        linterCompartment.of(getLintExtension(onLintCode)),
    );

    // The editor state should only be recreated when we're rendering a new file
    // (i.e., when the filename prop or initialCode changes) because it will
    // trigger creating an entirely new editor view, which resets the user's
    // selection and cursor.
    const editorState = useMemo(() => {
        return EditorState.create({
            doc: initialCode,
            extensions: [
                ...getPythonExtensions(),
                lintExtension.current,
                updateListenerExtension.current,
                FilenameFacet.of(filename),
            ],
            selection: fileStatesRef.current.get(fileKey)?.selection,
        });
    }, [initialCode, filename, fileKey]);

    // This needs to live in an effect because the container ref is not available
    // on the initial render, and there doesn't seem to be a way to update the
    // parent for the editor view after it's been created.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const editorView = new EditorView({
            state: editorState,
            parent: containerRef.current,
            scrollTo: fileStatesRef.current.get(fileKey)?.scrollSnapshot,
        });
        editorViewRef.current = editorView;

        return () => {
            // Clean up the old editor view.
            if (editorView != null) {
                editorView.destroy();
            }
        };
    }, [editorState, fileKey]);

    useEffect(() => {
        const editorView = editorViewRef.current;
        if (!editorView) {
            return;
        }

        // Notify the editor view whenever our update listener or linter changes.
        editorView.dispatch({
            effects: [
                updateListenerCompartment.reconfigure(updateListener),
                linterCompartment.reconfigure(getLintExtension(onLintCode)),
            ],
        });
    }, [
        updateListenerCompartment,
        updateListener,
        linterCompartment,
        onLintCode,
    ]);

    // We can't use a Wonder Blocks View here, because it doesn't work
    // with the way CodeMirror renders into its parent.
    return (
        <div
            ref={containerRef}
            data-testid="editor-container"
            className={css(styles.editorContainer)}
        />
    );
};

const styles = StyleSheet.create({
    editorContainer: {
        overflow: "hidden",
        flexGrow: 1,
    },
});

export default CodeMirrorEditor;
